import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

// MESSAGES
import ProvidersFormattedMessages from '../../messages/admin/ProvidersFormattedMessages';

// COMPONENTS
import Layout from '../../components/layouts/Layout';
import ProvidersTableRow from '../../components/admin/providers/ProvidersTableRow';
import ProvidersSingle from '../../components/admin/providers/ProvidersSingle';
import ProvidersAddForm from '../../components/admin/providers/ProvidersAddForm';
import Loader from '../../components/common/Loader';

// NETWORK
import useProvidersRequest from '../../requests/ProviderRequest';

// ROUTES
import { AdminRoutes } from '../../routes/AdminRoutes';

const AdminProvider = ({
  customer,
  location,
  errorHandler,
  updateActingProviderDetails,
  accessLevel,
  accessType,
}) => {
  const [providers, setProviders] = useState([]);
  const [showSingleProvider, setShowSingleProvider] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageTitle, setPageTitle] = useState(
    ProvidersFormattedMessages.listOfProviders
  );
  const { getAllProviders } = useProvidersRequest();

  useEffect(() => {
    if (customer) {
      fetchProviders();
    }
  }, [customer]);

  const fetchProviders = async () => {
    try {
      let providers = await getAllProviders(errorHandler);
      const sortedProviders = sortProviders(providers);
      setProviders(sortedProviders);
      setShowSingleProvider(false);
      setSelectedProvider(null);
      setShowAddProvider(false);

      if (location.state?.selectedProvider) {
        const selected = sortedProviders.find(
          (p) => p.provider_id === location.state.selectedProvider
        );
        if (selected) {
          setSelectedProvider(selected);
          setShowSingleProvider(true);
          location.state.selectedProvider = null;
        }
      }
      setLoading(false);
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  const sortProviders = (providers) => {
    // Store provider per status
    const categories = { A: [], T: [], S: [], C: [] };
    providers.forEach((p) => categories[p.provider_status]?.push(p));

    // Sort each category alphabetically
    return [
      ...categories.A.sort((a, b) =>
        a.provider_name.localeCompare(b.provider_name)
      ),
      ...categories.T.sort((a, b) =>
        a.provider_name.localeCompare(b.provider_name)
      ),
      ...categories.S.sort((a, b) =>
        a.provider_name.localeCompare(b.provider_name)
      ),
      ...categories.C.sort((a, b) =>
        a.provider_name.localeCompare(b.provider_name)
      ),
    ];
  };

  const handleBackButtonClick = (refreshFlag) => {
    if (refreshFlag) fetchProviders();
    setShowSingleProvider(false);
    setSelectedProvider(null);
    setShowAddProvider(false);
    setPageTitle(ProvidersFormattedMessages.listOfProviders);
  };

  const generateTable = () => (
    <TableContainer sx={{ marginBottom: 4, overflowX: 'auto' }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{ProvidersFormattedMessages.nameText}</TableCell>
            <TableCell>{ProvidersFormattedMessages.statusText}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {providers.map((provider) => (
            <ProvidersTableRow
              key={provider.provider_id}
              id={provider.provider_id}
              name={provider.provider_name}
              active={provider.provider_status_name}
              provider={provider}
              onClickName={() => {
                setSelectedProvider(provider);
                setShowSingleProvider(true);
              }}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (accessLevel === '3' && accessType === '3') {
    return (
      <Layout
        title={pageTitle}
        routes={AdminRoutes}
        accessLevel={accessLevel}
        accessType={accessType}
        backButtonText={
          !showSingleProvider && !selectedProvider && !showAddProvider
            ? ''
            : ProvidersFormattedMessages.backText
        }
        handleBackButton={handleBackButtonClick}
        titleRowRightComponent={
          !showSingleProvider &&
          !selectedProvider &&
          !showAddProvider && (
            <Box>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setShowAddProvider(true)}
              >
                {ProvidersFormattedMessages.addNewText}
              </Button>
            </Box>
          )
        }
      >
        {error && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Loader />
        ) : showSingleProvider && selectedProvider ? (
          <ProvidersSingle
            provider={selectedProvider}
            errorHandler={errorHandler}
            updateActingProviderDetails={updateActingProviderDetails}
            setPageTitle={setPageTitle}
          />
        ) : showAddProvider ? (
          <ProvidersAddForm
            backButtonClick={handleBackButtonClick}
            errorHandler={errorHandler}
            setPageTitle={setPageTitle}
          />
        ) : (
          generateTable()
        )}
      </Layout>
    );
  }

  return null;
};

export default AdminProvider;
